import { SyntheticEvent, useCallback, useState, type ReactNode } from "react";
import { Body, Link, SectionHeader } from "@sprout/typography";
import CompanyLogo from "@components/company_logo";
import { useQueryClient } from "@tanstack/react-query";
import { router } from "@inertiajs/react";
import SearchIcon from "@/sprout/icons/search";
import { TextInput } from "@sprout/text_input.tsx";
import "./subscription.scss";
import useTranslation from "@hooks/use_translation";
import Container from "@components/ui/container.tsx";
import BackButton from "@components/back_button.tsx";
import KeywordTags from "@components/keyword_tags.tsx";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/ui/dialog.tsx";
import { Trans } from "react-i18next";
import ErrorIcon from "@sprout/icons/error.tsx";
import { EnableAlertsModal } from "@components/job_alerts/enable_alerts_modal.tsx";
import HelpPopover from "@sprout/help_popover.tsx";
import { MultiSelect, type Option } from "@sprout/select";
import axios, { isAxiosError } from "axios";
import { DangerButton, DestructiveButton, PrimaryButton, SecondaryButton } from "@sprout/buttons";

type Method = "PUT" | "POST";

type Props = {
  departments: Option[];
  selected: Option[];
  pageTitle: string;
  subscriptionPath: string;
  subscriptionsPath: string;
  subscriptionId: number;
  method: Method;
  logoUrl?: string;
  companyName: string;
  allowDepartmentAlerts: boolean;
  keywords: string[];
  jobAlertsPaused: boolean;
  emailPreferencesPath?: string;
};

const ConfirmationModal = ({
  onConfirm,
  children,
}: {
  onConfirm: () => void;
  children: ReactNode;
}) => {
  return (
    <Dialog>
      <DialogTrigger>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="leading-[1.5rem]">
            <div className="flex gap-4">
              <ErrorIcon color="error-red" />
              <Trans i18nKey="jobAlerts:subscription:deleteModal:title" />
            </div>
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>
          <Body>
            <Trans i18nKey="jobAlerts:subscription:deleteModal:description" />
          </Body>
        </DialogDescription>
        <DialogFooter>
          <DialogClose>
            <SecondaryButton>
              <Trans i18nKey="jobAlerts:subscription:deleteModal:cancel" />
            </SecondaryButton>
          </DialogClose>
          <DialogClose>
            <DestructiveButton onClick={onConfirm}>
              <Trans i18nKey="jobAlerts:subscription:deleteModal:confirm" />
            </DestructiveButton>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Subscription = ({
  departments,
  method,
  selected,
  pageTitle,
  subscriptionPath,
  subscriptionsPath,
  subscriptionId,
  logoUrl,
  companyName,
  keywords,
  allowDepartmentAlerts,
  jobAlertsPaused,
  emailPreferencesPath,
}: Props) => {
  const { t } = useTranslation("jobAlerts");
  const [isDirty, setIsDirty] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState<Option[]>(selected);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formKeywords, setFormKeywords] = useState<string[]>(keywords || []);
  const [newKeyword, setNewKeyword] = useState<string>("");
  const [keywordError, setKeywordError] = useState<string | null>(null);
  const [departmentError, setDepartmentError] = useState<string | null>(null);
  const [showPausedModal, setShowPausedModal] = useState(method === "POST" && jobAlertsPaused);
  const queryClient = useQueryClient();

  const isKeywordEmpty = newKeyword.trim().length === 0;

  const submitSubscriptionSettings = useCallback(async () => {
    if (isSubmitting) {
      return;
    }
    if (selectedDepartments.length > 8) {
      setDepartmentError(t("subscription.department.too_many"));
      return;
    }
    setIsSubmitting(true);

    try {
      const payload = {
        id: subscriptionId,
        departments: selectedDepartments.map((department: Option) => department.value),
        keywords: formKeywords,
      };

      await axios({
        method,
        url: subscriptionPath,
        data: payload,
      });

      setIsDirty(false);
      await queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
      method === "POST" ? router.visit("/dashboard") : router.visit("/subscriptions");
    } catch (e) {
      const errorMessage = (isAxiosError(e) && e.response?.data?.message) ?? t("defaultError");
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, selectedDepartments, queryClient, router, formKeywords]);

  const onSelect = useCallback(
    (values: Option[]) => {
      if (values.length > 8) {
        setDepartmentError(t("subscription.department.too_many"));
        return;
      }

      setDepartmentError(null);
      setSelectedDepartments(values);
      setIsDirty(true);
    },
    [selectedDepartments],
  );

  const handleDelete = useCallback(async () => {
    if (isSubmitting) {
      return;
    }

    try {
      await axios.delete(subscriptionPath);
      await queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
      router.visit("/subscriptions");
    } catch (e) {
      console.error(e);
      setError(t("defaultError"));
      setKeywordError(t("defaultError"));
    }
  }, [subscriptionPath, queryClient, t]);

  const addKeyword = (e: SyntheticEvent) => {
    e.preventDefault();

    // Prevent exceeding max keywords (8)
    if (formKeywords.length >= 8) {
      setKeywordError(t("subscription.keywords.too_many"));
      return;
    }

    const trimmed = newKeyword.trim();
    const normalizedNew = trimmed.toLowerCase();
    const alreadyExists = formKeywords.some((k) => k.trim().toLowerCase() === normalizedNew);
    setNewKeyword("");
    setKeywordError(null);

    if (trimmed.length === 0 || alreadyExists) {
      return;
    }

    setFormKeywords((prevKeywords) => prevKeywords.concat([trimmed]));
    setIsDirty(true);
  };

  const removeKeyword = (label: string) => {
    setFormKeywords((prevKeywords) => {
      const index = keywords.indexOf(label);
      if (index !== -1) {
        setIsDirty(true);
        setKeywordError(null);
        return [...prevKeywords.slice(0, index), ...prevKeywords.slice(index + 1)];
      }
      return prevKeywords;
    });
  };

  return (
    <Container>
      <BackButton href={subscriptionsPath} />
      <CompanyLogo companyName={companyName} logoUrl={logoUrl} />
      <div className="flex flex-col gap-[4px]">
        <SectionHeader as="h1">{pageTitle}</SectionHeader>
        <Body>{t("subscription.selectJobs")}</Body>
      </div>
      <div className="subscription">
        {allowDepartmentAlerts && departments.length > 0 && (
          <div className="subscription__input">
            <MultiSelect
              values={selectedDepartments}
              label={t("subscription.department.label")}
              onChange={onSelect}
              options={departments}
              clearable
              error={departmentError}
            />
          </div>
        )}

        <div className="subscription-keyword gap-[8px]">
          <div className="subscription-keyword__input-row gap-[12px]">
            <div className="subscription-keyword__input-mobile-row gap-[12px]">
              <div className="subscription-keyword__input">
                <TextInput
                  id="keyword"
                  placeholder={t("subscription.keywords.placeholder")}
                  maxLength={255}
                  short={true}
                  onChange={(event) => setNewKeyword(event.currentTarget.value || "")}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      addKeyword(event);
                    }
                  }}
                  value={newKeyword}
                  narrow={true}
                  icon={<SearchIcon />}
                  error={keywordError}
                  help={t("subscription.keywords.subtitle")}
                />
              </div>
              <div className="subscription subscription-keyword__button">
                <SecondaryButton
                  onClick={addKeyword}
                  disabled={!!keywordError || isKeywordEmpty}
                  size="medium"
                >
                  {t("subscription.keywords.add")}
                </SecondaryButton>
              </div>
              <HelpPopover text={t("subscription.keywords.helperText")} className="h-[40px]" />
            </div>
          </div>
          <KeywordTags keywords={formKeywords} onClick={removeKeyword} />
        </div>

        {error && (
          <p className="pb-[3px] helper-text helper-text--error" aria-live="polite">
            {error}
          </p>
        )}
        <div className="subscription__buttons">
          <PrimaryButton
            onClick={() => submitSubscriptionSettings()}
            loading={isSubmitting}
            disabled={!isDirty || !(formKeywords.length || selectedDepartments.length)}
          >
            {method === "POST" ? t("subscription.create") : t("subscription.update")}
          </PrimaryButton>
          {method !== "POST" && (
            <ConfirmationModal onConfirm={handleDelete}>
              <DangerButton>{t("subscription.delete")}</DangerButton>
            </ConfirmationModal>
          )}
          <Link href={subscriptionsPath}>{"Cancel"}</Link>
        </div>
      </div>
      {emailPreferencesPath && (
        <EnableAlertsModal
          open={showPausedModal}
          onOpenChange={setShowPausedModal}
          companyName={companyName}
          emailPreferencesPath={emailPreferencesPath}
          onDismiss={() => setShowPausedModal(false)}
        />
      )}
    </Container>
  );
};

export default Subscription;
